import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { useBookAppointment } from "@/lib/api-client-react";
import { CreditCard, Calendar, Clock, CheckCircle2, ShieldCheck, Package, Loader2, Sparkles, Stethoscope } from "lucide-react";
import { useLocation } from "wouter";

interface QuickBundleCheckoutModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bundle: any;
  providerInfo?: any;
}

const TIME_SLOTS = [
  "09:00 AM",
  "10:00 AM",
  "11:00 AM",
  "01:00 PM",
  "02:00 PM",
  "03:00 PM",
  "04:00 PM"
];

export default function QuickBundleCheckoutModal({
  open,
  onOpenChange,
  bundle,
  providerInfo
}: QuickBundleCheckoutModalProps) {
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const bookAppointment = useBookAppointment();

  const tomorrow = new Date(Date.now() + 86400000).toISOString().split("T")[0];
  const [appointmentDate, setAppointmentDate] = useState<string>(tomorrow);
  const [startTime, setStartTime] = useState<string>("09:00 AM");
  const [patientNotes, setPatientNotes] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  if (!bundle) return null;

  const userJson = localStorage.getItem("user");
  const user = userJson ? JSON.parse(userJson) : null;

  const totalAmount = Number(bundle.finalPrice ?? bundle.totalPrice ?? 0);
  const originalAmount = Number(bundle.totalPrice ?? 0);
  const hasDiscount = bundle.discountPercent > 0 && originalAmount > totalAmount;
  const providerName = bundle.providerName || bundle.providerInfo?.name || providerInfo?.displayName || providerInfo?.name || "Healthcare Provider";

  const handleCheckout = async () => {
    if (!user) {
      toast({
        title: "Sign In Required",
        description: "Please sign in to your account to complete your bundle checkout.",
        variant: "destructive"
      });
      onOpenChange(false);
      setLocation("/login");
      return;
    }

    setIsSubmitting(true);

    try {
      const providerId = bundle.providerId || bundle.providerInfo?.id || bundle.providerInfo?.providerId || providerInfo?.id;

      await bookAppointment.mutateAsync({
        providerId,
        bundleId: bundle.id,
        appointmentDate: appointmentDate || tomorrow,
        startTime: startTime || "09:00 AM",
        notes: patientNotes ? `[DIRECT BUNDLE CHECKOUT] ${patientNotes}` : "[DIRECT BUNDLE CHECKOUT]",
        amount: totalAmount
      });

      toast({
        title: "Order & Booking Confirmed! 🎉",
        description: `Successfully booked "${bundle.name}" for ${appointmentDate || tomorrow} at ${startTime}.`
      });

      onOpenChange(false);
    } catch (err: any) {
      console.error("Checkout error:", err);
      toast({
        variant: "destructive",
        title: "Checkout Failed",
        description: err.message || "Failed to complete checkout. Please try again."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-2rem)] sm:w-full max-w-lg max-h-[90vh] sm:max-h-[86vh] flex flex-col p-0 overflow-hidden rounded-2xl sm:rounded-3xl border-border bg-card shadow-2xl [&>button]:text-white [&>button]:opacity-80 [&>button:hover]:opacity-100 [&>button]:top-5 [&>button]:right-5">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 p-5 sm:p-6 text-white relative shrink-0">
          <div className="flex items-center gap-2 mb-2">
            <span className="p-2 rounded-xl bg-white/15 backdrop-blur-md">
              <CreditCard className="w-5 h-5 text-white" />
            </span>
            <span className="text-xs font-extrabold uppercase tracking-wider text-indigo-100">
              Direct Checkout
            </span>
          </div>
          <DialogTitle className="text-xl font-black text-white tracking-tight pr-6">
            {bundle.name}
          </DialogTitle>
          <DialogDescription className="text-xs text-indigo-100/90 mt-1 line-clamp-1 pr-6">
            {bundle.description || "Comprehensive service package for direct care."}
          </DialogDescription>
        </div>

        <div className="p-4 sm:p-6 space-y-4 sm:space-y-5 overflow-y-auto flex-1 min-h-0 scrollbar-thin">
          {/* Bundle Info Card */}
          <div className="bg-slate-50 dark:bg-slate-800/60 p-4 rounded-2xl border border-slate-200/80 dark:border-slate-800 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-indigo-100 dark:bg-indigo-950 text-indigo-700 dark:text-indigo-300">
                <Package className="w-3.5 h-3.5 text-indigo-500" />
                {bundle.category || "Service Package"}
              </span>
              {hasDiscount && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-extrabold bg-emerald-100 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300">
                  <Sparkles className="w-3 h-3" /> Save {bundle.discountPercent}%
                </span>
              )}
            </div>

            <div className="flex items-center gap-2 text-xs font-semibold text-slate-700 dark:text-slate-200">
              <Stethoscope className="w-4 h-4 text-indigo-500 shrink-0" />
              <span>Provider: <strong className="text-slate-900 dark:text-white">{providerName}</strong></span>
            </div>

            {/* Included CPT Services */}
            {bundle.cptCodes && bundle.cptCodes.length > 0 && (
              <div className="pt-2 border-t border-slate-200/60 dark:border-slate-700/60">
                <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block mb-1.5">
                  Included Services ({bundle.cptCodes.length}):
                </span>
                <div className="space-y-1">
                  {bundle.cptCodes.slice(0, 3).map((cpt: any, idx: number) => (
                    <div key={idx} className="flex justify-between text-xs text-slate-600 dark:text-slate-300">
                      <span className="truncate pr-2">• {cpt.codeShortDesc || cpt.codeLongDesc || cpt.code}</span>
                      <span className="font-mono text-slate-400">${Number(cpt.standardPrice || 0).toFixed(2)}</span>
                    </div>
                  ))}
                  {bundle.cptCodes.length > 3 && (
                    <span className="text-[11px] text-indigo-600 dark:text-indigo-400 font-semibold block">
                      + {bundle.cptCodes.length - 3} more items included
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Date & Time Selection */}
          <div className="space-y-3">
            <div className="flex items-center gap-1.5 text-xs font-bold text-slate-800 dark:text-slate-200">
              <Calendar className="w-4 h-4 text-indigo-600" />
              <span>Select Appointment Date & Time</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] font-bold text-slate-500 mb-1 block">Preferred Date</label>
                <Input
                  type="date"
                  min={tomorrow}
                  value={appointmentDate}
                  onChange={(e) => setAppointmentDate(e.target.value)}
                  className="h-10 text-xs rounded-xl font-medium"
                />
              </div>

              <div>
                <label className="text-[11px] font-bold text-slate-500 mb-1 block">Preferred Time Slot</label>
                <div className="relative">
                  <select
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    className="w-full h-10 px-3 text-xs font-medium rounded-xl border border-input bg-background text-foreground shadow-2xs focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                  >
                    {TIME_SLOTS.map((slot) => (
                      <option key={slot} value={slot}>
                        {slot}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Notes for Provider */}
          <div>
            <label className="text-[11px] font-bold text-slate-500 mb-1 block">Notes for Provider (Optional)</label>
            <Textarea
              placeholder="Add any medical notes or questions..."
              value={patientNotes}
              onChange={(e) => setPatientNotes(e.target.value)}
              className="text-xs rounded-xl h-16 resize-none"
            />
          </div>

          {/* Price & Security Summary */}
          <div className="p-4 rounded-2xl bg-indigo-50/70 dark:bg-indigo-950/40 border border-indigo-100 dark:border-indigo-900/50 flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-slate-600 dark:text-slate-300">Total Price:</span>
              <div className="flex items-baseline gap-2">
                {hasDiscount && (
                  <span className="text-xs text-slate-400 line-through font-medium">
                    ${originalAmount.toFixed(2)}
                  </span>
                )}
                <span className="text-2xl font-black text-indigo-700 dark:text-indigo-300">
                  ${totalAmount.toFixed(2)}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-1.5 text-[11px] text-emerald-700 dark:text-emerald-400 font-semibold pt-1 border-t border-indigo-100/80 dark:border-indigo-900/40">
              <ShieldCheck className="w-4 h-4 shrink-0 text-emerald-600" />
              <span>Encrypted 256-Bit SSL Checkout • All-Inclusive Guaranteed Rate</span>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="p-3.5 sm:p-4 bg-slate-50 dark:bg-slate-800/80 border-t border-slate-200 dark:border-slate-800 flex items-center justify-end gap-3 shrink-0">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
            className="text-xs font-bold rounded-xl h-10 px-4"
          >
            Cancel
          </Button>
          <Button
            onClick={handleCheckout}
            disabled={isSubmitting}
            className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-xl h-10 px-5 shadow-md gap-2"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Processing...</span>
              </>
            ) : (
              <>
                <CheckCircle2 className="w-4 h-4" />
                <span>Confirm & Pay ${totalAmount.toFixed(2)}</span>
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
